import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type { EmployeePayrollStatusEnum } from "@/utils/enums/payroll.enum";

export type IEmployeePayslipDetail = {
  id: number;
  runId: number;
  userId: number;
  title: string;
  header: {
    employee: {
      id: number;
      fullName: string;
      firstName: string;
      lastName: string;
      employeeCode: string;
      profileImage: string | null;
      nationality: { id: number; name: string; code: string } | null;
    };
    branch: { id: number; name: string } | null;
    department: { id: number; name: string } | null;
    accountMasked: string | null;
    periodLabel: string;
  };
  payslipDetail: {
    earnings: { name: string; amount: number | string }[];
    grossPay: number | string;
    deductions: { name: string; subtitle?: string; amount: number | string }[];
  };
  employmentInformation: {
    employerObligations: {
      grossPaid: number | string;
      statutoryEmployer: number | string;
      totalCost: number | string;
    };
    employeeDetails: {
      nationality: string;
      country: { code: string; name: string };
      serviceYears: number | string;
      joinDate: string;
    };
    payBreakdown: {
      basePay: number | string;
      bonus: number | string;
      deductions: number | string;
    };
  };
  netPay: {
    amount: number | string;
    currency: { id: number; code: string; symbol: string };
    payDate: string;
  };
  payslipGeneratable: boolean;
  runStatus: string;
  receiptSignatureStatus: "signed" | "pending" | null;
  payslipPreview?: import("@/types/(company)/payrollRuns.interface").IPayslipPreview | null;
};

export type IEmployeePayrollSummary = {
  currentMonthNetSalary: { amount: number; currency: string };
  totalEarningsThisYear: { amount: number; currency: string };
  deductionsThisMonth: { amount: number; currency: string };
  lastPaymentStatus: string;
};

export type IEmployeePayslipListItem = {
  id: number;
  runId: number;
  payPeriod: string;
  grossSalary: number | string;
  overtimePay: number | string;
  deductions: number | string;
  contribution: number | string;
  netSalary: number | string;
  payDate: string;
  status: EmployeePayrollStatusEnum | string;
  hasPayslip: boolean;
  currency: { code: string; symbol: string } | null;
  receiptSignatureStatus: "signed" | "pending" | null;
};

export type IListEmployeePayslipsParams = {
  year?: number;
  page?: number;
  limit?: number;
  search?: string;
  status?: EmployeePayrollStatusEnum;
  sort?: string;
  sortOrder?: "asc" | "desc";
};

export const employeePayrollApi = createApi({
  reducerPath: "employeePayrollApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["msPayrollSummary", "msPayslips", "msPayslip"],
  endpoints: (builder) => ({
    getEmployeePayrollSummary: builder.query<
      IApiResponse<IEmployeePayrollSummary>,
      { year?: number } | void
    >({
      query: (params) => ({
        url: "/employee/payroll/summary",
        method: "GET",
        params: params?.year ? { year: params.year } : {},
      }),
      providesTags: ["msPayrollSummary"],
    }),
    listEmployeePayslips: builder.query<
      IApiPaginatedResponse<IEmployeePayslipListItem[]>,
      IListEmployeePayslipsParams | void
    >({
      query: (params) => ({
        url: "/employee/payroll/payslips",
        method: "GET",
        params: {
          page:  params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.year ? { year: params.year } : {}),
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.sort ? { sort: params.sort } : {}),
          ...(params?.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
      }),
      providesTags: ["msPayslips"],
    }),
    getEmployeePayslipDetail: builder.query<
      IApiResponse<IEmployeePayslipDetail | null>,
      number
    >({
      query: (id) => ({
        url: `/employee/payroll/payslips/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "msPayslip", id }],
    }),
    getReceiptSignUrl: builder.query<
      IApiResponse<{ url: string | null; status: "pending" | "signed" | "none" }>,
      number
    >({
      query: (id) => ({
        url: `/employee/payroll/payslips/${id}/receipt-sign-url`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetEmployeePayrollSummaryQuery,
  useListEmployeePayslipsQuery,
  useGetEmployeePayslipDetailQuery,
  useGetReceiptSignUrlQuery,
} = employeePayrollApi;
